/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
	useState,
	Fragment,
	useEffect,
	useRef,
	useCallback,
	useMemo,
	memo,
	type Dispatch,
	type SetStateAction,
} from 'react'

import { ChevronDown20Regular, LockClosed16Filled } from '@fluentui/react-icons'
import { Combobox, Transition } from '@headlessui/react'
import type { FilterStateProps } from '@hooks/useQuestion'
import { styled } from '@styles/theme'
import { useVirtualizer } from '@tanstack/react-virtual'

import { CheckBox } from '../CheckBox'
import { autoCompleteStyles } from './styles'

const {
	ComboboxButton,
	ComboboxButtonDisabled,
	ComboboxOptions,
	ComboboxOption,
	comboboxInput,
	ContainerComboboxInput,
	ScrollOptions,
	NumberSelected,
} = autoCompleteStyles

const Error = styled('span', autoCompleteStyles.error)

type StitchesProps = React.ComponentProps<typeof ComboboxButton>
type OptionsProps = {
	name: string
	value?: number | string | any
	[key: string]: any
}

interface SelectProps extends StitchesProps {
	error?: string
	options?: Array<OptionsProps>
	disabled?: boolean
	placeholder?: string
	selectedText?: string
	onChange: Dispatch<SetStateAction<FilterStateProps>> | undefined
	name: string
	selectedValue?: any
	onSearch?: (term: string) => void
	multiple?: boolean
}

const useDebounce = <T,>(value: T, delay: number): T => {
	const [debouncedValue, setDebouncedValue] = useState(value)

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value)
		}, delay)

		return () => {
			clearTimeout(handler)
		}
	}, [value, delay])

	return debouncedValue
}

const VirtualizedOption = memo(
	({ item, multiple }: { item: OptionsProps; multiple: boolean }) => (
		<Combobox.Option className={ComboboxOption()} value={item}>
			{({ selected }) =>
				multiple ? (
					<CheckBox label={item.name} checked={selected} />
				) : (
					<span
						style={{
							fontWeight: selected ? '600' : '400',
							padding: '8px 12px',
							display: 'block',
							cursor: 'pointer',
						}}
					>
						{item.name}
					</span>
				)
			}
		</Combobox.Option>
	)
)

const VirtualizedList = memo(
	({ items, multiple }: { items: Array<OptionsProps>; multiple: boolean }) => {
		const parentRef = useRef<HTMLDivElement>(null)

		const virtualizer = useVirtualizer({
			count: items.length,
			getScrollElement: () => parentRef.current,
			estimateSize: () => 50,
			overscan: 10,
		})

		const rowItems = virtualizer.getVirtualItems()

		return (
			<ScrollOptions ref={parentRef}>
				<div
					style={{
						height: `${virtualizer.getTotalSize()}px`,
						width: '100%',
						position: 'relative',
					}}
				>
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							transform: `translateY(${rowItems[0]?.start || 0}px)`,
						}}
					>
						{rowItems.map((virtualRow) => {
							const item = items[virtualRow.index]

							return (
								<VirtualizedOption
									key={item.value || item.id || virtualRow.index}
									item={item}
									multiple={multiple}
								/>
							)
						})}
					</div>
				</div>
			</ScrollOptions>
		)
	}
)

const removeAccents = (str: string) => {
	return str
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/\s+/g, '')
}

export const AutocompleteBase = memo(
	({
		error,
		options = [],
		disabled,
		placeholder = 'Selecione',
		selectedText = 'Disciplina...',
		onChange,
		name,
		selectedValue,
		onSearch,
		multiple = true,
		...props
	}: SelectProps) => {
		const [query, setQuery] = useState('')
		const debouncedQuery = useDebounce(query, 300)

		const safeSelectedValue = useMemo(() => {
			if (multiple) return Array.isArray(selectedValue) ? selectedValue : []

			return selectedValue || null
		}, [selectedValue, multiple])

		const filteredOptions = useMemo(() => {
			if (!debouncedQuery) return options

			const cleanQuery = removeAccents(debouncedQuery)

			return options.filter((item) =>
				removeAccents(item.name).includes(cleanQuery)
			)
		}, [options, debouncedQuery])

		const handleChange = useCallback(
			(val: OptionsProps[] | OptionsProps | null) => {
				if (onChange) {
					onChange((prevState: any) => ({
						...prevState,
						[name]: val,
					}))
				}
			},
			[onChange, name]
		)

		useEffect(() => {
			onSearch?.(query)
		}, [query, onSearch])

		const renderButtonContent = () => {
			if (multiple) {
				const count = Array.isArray(safeSelectedValue)
					? safeSelectedValue.length
					: 0

				return (
					<>
						{count > 0 ? selectedText : placeholder}
						<div style={{ display: 'flex', gap: '16px', marginLeft: '12px' }}>
							{count > 0 && <NumberSelected>{count}</NumberSelected>}
						</div>
					</>
				)
			}

			return (
				<span
					style={{
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
						maxWidth: '85%',
					}}
				>
					{safeSelectedValue && 'name' in safeSelectedValue
						? safeSelectedValue.name
						: placeholder}
				</span>
			)
		}

		const renderIcons = () => (
			<div
				style={{
					display: 'flex',
					gap: '8px',
					marginLeft: 'auto',
					alignItems: 'center',
				}}
			>
				{disabled && <LockClosed16Filled />}
				<ChevronDown20Regular />
			</div>
		)

		return (
			<div style={{ position: 'relative', width: '100%' }}>
				<Combobox
					value={safeSelectedValue}
					onChange={handleChange as any}
					disabled={disabled}
					multiple={multiple as any}
					{...props}
				>
					{disabled ? (
						<ComboboxButtonDisabled>
							{renderButtonContent()}
							{renderIcons()}
						</ComboboxButtonDisabled>
					) : (
						<ComboboxButton>
							{renderButtonContent()}
							{renderIcons()}
						</ComboboxButton>
					)}

					<Transition
						as={Fragment}
						leave='transition ease-in duration-100'
						leaveFrom='opacity-100'
						leaveTo='opacity-0'
						afterLeave={() => setQuery('')}
					>
						<ComboboxOptions>
							<ContainerComboboxInput>
								<Combobox.Input
									placeholder='Pesquisar'
									className={comboboxInput()}
									onChange={(e) => setQuery(e.target.value)}
									value={query}
								/>
							</ContainerComboboxInput>

							{filteredOptions.length === 0 && query ? (
								<div style={{ padding: '10px', textAlign: 'center' }}>
									Nenhum resultado encontrado
								</div>
							) : (
								<VirtualizedList items={filteredOptions} multiple={multiple} />
							)}
						</ComboboxOptions>
					</Transition>
				</Combobox>

				{error && <Error>{error}</Error>}
			</div>
		)
	}
)
